import React, { memo, useEffect } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import { faArrowRight, faMapMarkedAlt } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CardPathDetails } from 'handimap-react-lib/components/Card/CardInfoDetails';
import { setSelectedPath } from 'handimap-react-lib/store/actions/directionsActions';
import { ObstaclesWarningContainer } from 'handimap-react-lib/components/ObstaclesWarningContainer';
import { DistanceBadge } from '../components/DistanceBadge';
import { FontAwesomeSvgIcon } from 'handimap-react-lib/components/FontAwesomeSvgIcon';
import { setContentType } from 'handimap-react-lib/store/actions/sideBarActions';
import { ShareButton } from '../components/ShareButton';
import { convertGeoJsonToGpx } from 'handimap-react-lib/utils/utils';
import { useSelector } from 'react-redux';
import { useLabelValue } from 'handimap-react-lib/utils/useLabelValue';
import { useResponsive } from 'handimap-react-lib/hooks/useResponsive';
import { PathDetails } from 'handimap-react-lib/components/PathDetails';
import { getDurations } from 'handimap-react-lib/utils/utils';
import { AccessibilityNoteContainer } from './AccessibilityNoteContainer';

function testAreEqual(prevRoad, nextRoad) {
  if(prevRoad.selectedPathFromUrl !== nextRoad.selectedPathFromUrl) {
    return false;
  } else if (prevRoad.status !== nextRoad.status) {
    return false;
  } else if (prevRoad.allResults !== nextRoad.allResults) {
    return false;
  } else if (prevRoad.selectedPath !== nextRoad.selectedPath) {
    return false;
  } 
  return true;
}

const Path = ({
  selectedPathFromUrl, 
  status, 
  allResults, 
  selectedPath,   
}) => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {setLabelProfile, setLabelCondition} = useLabelValue();
  const { isMobile } = useResponsive();

  const profil = useSelector(state => state.profils.profil);
  const condition = useSelector(state => state.profils.condition);
  const origin = useSelector(state => state.directions.origin);
  const dest = useSelector(state => state.directions.destination);
  const multimodal = useSelector(state => state.profils.multimodal);

  const clickCardCallBack = (cardNum) => {
    dispatch(setSelectedPath(cardNum));
  };

  const openRoadMap = (cardNum) => {
    dispatch(setContentType('stepsDetails'));
    dispatch(setSelectedPath(cardNum));
  };

  const handleExportGpx = (i) => {
    const label = `${t('common.itinerary')}_${i + 1}_${setLabelProfile(profil)}${condition && `_${setLabelCondition(condition)}`}`;
    const namePosition = `${t('common.gps_location', {origin: origin.label, dest: dest.label})}`;
    convertGeoJsonToGpx(allResults[i].geojson, label, namePosition, origin, dest);
  };

  useEffect(() => {
    if (selectedPathFromUrl !== null && selectedPathFromUrl !== undefined && selectedPathFromUrl !== '') {
      dispatch(setSelectedPath(parseInt(selectedPathFromUrl)));
    }
  }, []);

  const styles = {
    loadingContainer : {
      display: 'flex',
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      paddingY: '1em'
    },
    resultsContainer : {
      display: 'flex',
      flexDirection: 'column',
      marginBlockStart: 0,
      marginBlockEnd: 0,
      paddingInlineStart: 0
    },
    cardInfoContainer: {
      display: 'flex',
      paddingX: '.5em',
      minHeight: '3em',
      alignItems: 'center'
    },
    cardContentContainer: {
      display: 'flex', 
      width: '100%',
      paddingX: '.5em !important',
      paddingY: '.7em !important'
    },
    labelContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    infoContainer : {
      display: 'flex',
      width: '100%',
    },
    headerContainer : {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    recapContainer: {
      display: 'flex',
      width: '100%',
      boxSizing: 'border-box',
      borderRadius: '.5em',
      justifyContent: 'center'
    },
    labelHeader: {
      fontSize: '1.6em',
      marginBottom: profil !== 'Marche' && '.5em',
      fontWeight: 700,
    },
    labelSub: {
      fontSize: '.9em',
    },
    detailsContainer: {
      display: 'flex',
      width: '100%',
      marginTop: '1em'
    },
    viewPathButton: {
      display: 'flex',
      width: '100%',
      backgroundColor: '#000000',
      borderRadius: '100vmax',
      flexWrap: 'wrap',
      color: '#FFFFFF',
      '&:hover': {
        backgroundColor: '#D3D3D3',
        textDecoration: 'underline',
      },
      '&:focus' : {
        textDecoration: 'underline',
      },
      fontSize: '.8em',
      paddingY: '.8em'
    },
    cardActionsContainer: {
      display: 'flex',
      width: '100%', 
      justifyContent: 'space-between', 
      padding: 0
    },
    actionButton: {
      textTransform: 'none',
      width: '45%',
      color: '#000000',
      fontSize: '.95em',
      textAlign: 'left !important',
      paddingX: '0 !important',
      '&:hover' : {
        textDecoration: 'underline',
      },
      '&:focus' : {
        textDecoration: 'underline',
      }
    }
  };

  return (
    <React.Fragment>
      {status === 'success' && (
        <Stack component='ul' sx={styles.resultsContainer} spacing={1}>
          {allResults.map((path, i) => {
            return (
              <CardPathDetails 
                key={i} 
                sx={styles.cardInfoContainer}
                component="li" 
                id={`path_${i}`} 
                active={selectedPath === i ? 'true' : 'false'}
              >
                <CardContent sx={styles.cardContentContainer}>
                  <Stack sx={styles.infoContainer} spacing={1}>
                    <CardActionArea onClick={() => clickCardCallBack(i)}>
                      <Box sx={styles.headerContainer}>
                        <Box sx={styles.labelContainer}>
                          <Typography sx={styles.labelHeader} gutterBottom>
                            {getDurations(path.time) ?? '1 min'}
                          </Typography>
                          {profil !== 'Marche' && (
                            <DistanceBadge length={allResults[i].length} />
                          )}
                        </Box>
                        {profil !== 'Marche' ? (
                          <AccessibilityNoteContainer notes={allResults[i].access_score} />
                        ) : (
                          <DistanceBadge length={allResults[i].length} />
                        )}
                      </Box>
                    </CardActionArea>
                    {selectedPath === i && allResults[i].obstacles && (
                      <ObstaclesWarningContainer label={t('obstacles.obstacles_alerts_summary')} />
                    )}
                    {multimodal && (
                      <Box sx={styles.recapContainer}>
                        <PathDetails data={path.geojson} />
                      </Box>
                    )}
                    <Collapse in={selectedPath === i} collapsedSize={0} unmountOnExit>
                      <Stack sx={styles.detailsContainer} spacing={2}>
                        <Button 
                          sx={styles.viewPathButton}
                          endIcon={(
                            <FontAwesomeSvgIcon icon={faArrowRight} 
                              sx={{width: '1em', height: '1em'}}
                            />
                          )}
                          onClick={() => openRoadMap(i)}
                        >
                          {t('pathcard.button-details')}
                        </Button>
                        <CardActions disableSpacing sx={styles.cardActionsContainer}>
                          <Button 
                            sx={styles.actionButton}
                            variant='text' 
                            size={isMobile ? 'small' : 'large'}
                            endIcon={<FontAwesomeSvgIcon icon={faMapMarkedAlt} />}
                            onClick={() => {
                              handleExportGpx(i);
                            }}
                          >
                            {t('export-data.button-gpx')}
                          </Button>
                          <ShareButton styles={styles.actionButton} />
                        </CardActions>
                      </Stack>
                    </Collapse>
                  </Stack>
                </CardContent>
              </CardPathDetails>
            );
          })} 
        </Stack>
      )}
      {status === 'loading' && (
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      )}
    </React.Fragment>
  );
};

export const PathsCardsDetails = memo(Path, testAreEqual);